import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Boxes } from 'lucide-react';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CampaignBudgetInput } from '@/components/glitch-ui/campaign-budget-input';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { LandingPageCombobox, type LandingPageEntry } from '@/components/glitch-ui/landing-page-combobox';

export const Route = createFileRoute('/_authenticated/_layout/components/forms')({
  component: FormsPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

const DEMO_LANDING_PAGES: LandingPageEntry[] = [
  { id: '_homepage', label: 'Glitch Ads', url: 'https://glitchads.ai', type: 'homepage' },
  { id: 'product_1', label: 'Campaign Manager', url: 'https://glitchads.ai/campaigns', type: 'product' },
  { id: 'product_2', label: 'Optimizations', url: 'https://glitchads.ai/optimizations', type: 'product' },
  { id: 'custom_1', label: 'Pricing', url: 'https://glitchads.ai/pricing', type: 'custom' },
];

function FormsPage() {
  const [selectedPage, setSelectedPage] = useState<LandingPageEntry | null>(null);

  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8">
        <div className="grid grid-cols-12 gap-1">
          <div className="col-span-9">
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-4">
                <h2 className="text-lg font-semibold text-gray-900">Form Inputs</h2>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="text">Text Input</Label>
                    <Input type="text" id="text" placeholder="Enter text..." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Input</Label>
                    <Input type="email" id="email" placeholder="Enter your email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password Input</Label>
                    <Input type="password" id="password" placeholder="Enter password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="number">Number Input</Label>
                    <Input type="number" id="number" placeholder="Enter number" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="select">Select</Label>
                    <Select>
                      <SelectTrigger id="select">
                        <SelectValue placeholder="Choose an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="option1">Option 1</SelectItem>
                        <SelectItem value="option2">Option 2</SelectItem>
                        <SelectItem value="option3">Option 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Campaign Budget Input</Label>
                    <CampaignBudgetInput
                      value={1000}
                      setValue={() => {}}
                      currency="USD"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Landing Page Combobox</Label>
                  <LandingPageCombobox
                    entries={DEMO_LANDING_PAGES}
                    value={selectedPage}
                    onSelect={(entry) => setSelectedPage(entry)}
                    placeholder="Search or paste a URL..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Checkboxes</Label>
                    <div className="flex flex-col">
                      <label htmlFor="checkbox1" className="flex items-center space-x-2 h-10 px-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                        <Checkbox id="checkbox1" />
                        <span className="text-sm text-gray-900">Option 1</span>
                      </label>
                      <label htmlFor="checkbox2" className="flex items-center space-x-2 h-10 px-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                        <Checkbox id="checkbox2" />
                        <span className="text-sm text-gray-900">Option 2</span>
                      </label>
                      <label htmlFor="checkbox3" className="flex items-center space-x-2 h-10 px-3 rounded-md cursor-not-allowed opacity-50">
                        <Checkbox id="checkbox3" disabled />
                        <span className="text-sm text-gray-900">Disabled Option</span>
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Radio Group</Label>
                    <RadioGroup defaultValue="option1">
                      <label htmlFor="radio1" className="flex items-center space-x-2 h-10 px-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                        <RadioGroupItem value="option1" id="radio1" />
                        <span className="text-sm text-gray-900">Option 1</span>
                      </label>
                      <label htmlFor="radio2" className="flex items-center space-x-2 h-10 px-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                        <RadioGroupItem value="option2" id="radio2" />
                        <span className="text-sm text-gray-900">Option 2</span>
                      </label>
                      <label htmlFor="radio3" className="flex items-center space-x-2 h-10 px-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                        <RadioGroupItem value="option3" id="radio3" />
                        <span className="text-sm text-gray-900">Option 3</span>
                      </label>
                    </RadioGroup>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="textarea">Textarea</Label>
                  <Textarea id="textarea" placeholder="Enter your message..." rows={4} />
                </div>
                <div className="flex justify-end pt-4">
                  <Button size="lg" className="px-8 py-6 bg-[#290BD9] hover:bg-[#290BD9]/90 text-white">SUBMIT FORM</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="col-span-3">
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-4">
                <h2 className="text-lg font-semibold text-gray-900">Switches</h2>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-6">
                {/* ToggleSwitch Component - Label Inside Toggle */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500 uppercase tracking-wider">Toggle Switch (Label Inside)</Label>
                  <div className="space-y-3">
                    {/* Left/OK State */}
                    <div className="flex items-center gap-3">
                      <ToggleSwitch
                        value="left"
                        leftLabel="OK"
                        rightLabel="Undo"
                        onChange={() => {}}
                      />
                      <span className="text-sm text-gray-500">Left state (green)</span>
                    </div>
                    {/* Right/Undo State */}
                    <div className="flex items-center gap-3">
                      <ToggleSwitch
                        value="right"
                        leftLabel="OK"
                        rightLabel="Undo"
                        onChange={() => {}}
                      />
                      <span className="text-sm text-gray-500">Right state (red)</span>
                    </div>
                  </div>
                </div>

                {/* Simple Toggle - Labels Outside */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500 uppercase tracking-wider">Simple Toggle (Labels Outside)</Label>
                  <div className="space-y-3">
                    {/* Approve State */}
                    <div className="flex items-center justify-start space-x-3">
                      <span className="text-xs font-medium text-gray-700">Approve</span>
                      <div className="relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer bg-green-600">
                        <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200"></div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">Undo</span>
                    </div>

                    {/* Undo State */}
                    <div className="flex items-center justify-start space-x-3">
                      <span className="text-xs font-medium text-gray-700">Approve</span>
                      <div className="relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer bg-red-600">
                        <div className="absolute top-0.5 right-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200"></div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">Undo</span>
                    </div>

                    {/* Disabled State */}
                    <div className="flex items-center justify-start space-x-3">
                      <span className="text-xs font-medium text-gray-700">Approve</span>
                      <div className="relative w-12 h-6 rounded-full transition-colors duration-200 bg-gray-300 cursor-not-allowed opacity-50">
                        <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200"></div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">Undo</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
